import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ScrollView, View } from 'react-native';

import { insertIntoArray } from '../../../../common/utils/utils';

import {
	Button,
	Dialog,
	Heading,
	Picker,
	Text,
	TextInput,
} from '../../../components';
import { useThemedStyles } from '../../../hooks/useThemedStyles';

/** Driver-initiated exact order for every unfinished, unstarted stop. */
const NextStopReasonDialog = ({
	isOpen,
	setIsOpen,
	stops,
	onSubmit,
	loading,
}) => {
	const styles = useThemedStyles(createStyles);
	const [orderedStopIds, setOrderedStopIds] = useState([]);
	const [reason, setReason] = useState('');
	const originalStopIds = useMemo(() => (stops || []).map(stop => stop.id), [stops]);
	const stopsById = useMemo(() => new Map((stops || []).map(stop => [stop.id, stop])), [stops]);
	const positionOptions = useMemo(() => originalStopIds.map((_, index) => ({
		label: `Position ${index + 1}`,
		value: index,
	})), [originalStopIds]);
	const hasOrderChanged = useMemo(() => (
		orderedStopIds.length === originalStopIds.length &&
		orderedStopIds.some((stopId, index) => stopId !== originalStopIds[index])
	), [orderedStopIds, originalStopIds]);

	useEffect(() => {
		if (isOpen) {
			setOrderedStopIds(originalStopIds);
			setReason('');
		}
	}, [isOpen, originalStopIds]);

	const close = useCallback(() => setIsOpen(false), [setIsOpen]);

	const submit = useCallback(() => {
		if (!hasOrderChanged || !reason.trim()) {
			return;
		}

		onSubmit(orderedStopIds, reason.trim());
	}, [hasOrderChanged, onSubmit, orderedStopIds, reason]);

	const moveStop = useCallback((stopId, destinationIndex) => {
		setOrderedStopIds((currentIds) => {
			const sourceIndex = currentIds.indexOf(stopId);
			const nextIndex = Number(destinationIndex);
			if (
				sourceIndex < 0 ||
				!Number.isInteger(nextIndex) ||
				nextIndex < 0 ||
				nextIndex >= currentIds.length ||
				sourceIndex === nextIndex
			) {
				return currentIds;
			}

			const remainingIds = currentIds.filter(currentId => currentId !== stopId);
			return insertIntoArray(remainingIds, nextIndex, stopId);
		});
	}, []);

	return (
		<Dialog isOpen={isOpen} onClose={close}>
			<View>
				<Heading heading="Reorder remaining stops" />
				<Text style={styles.subtleText}>Choose a position among the remaining stops. Completed and in-progress stops stay fixed.</Text>
				<ScrollView style={styles.stopList} scrollIndicatorInsets={{ right: 1 }}>
					{
						orderedStopIds.map((stopId, index) => {
							const stop = stopsById.get(stopId);
							if (!stop) {
								return null;
							}

							return (
								<View
									key={stop.id}
									style={styles.stopRow}
								>
									<View style={styles.stopOrder}>
										<Text style={styles.stopOrderText}>{index + 1}</Text>
									</View>
									<Text style={styles.stopRowText}>{stop.name}</Text>
									<View style={styles.positionControl}>
										<Text style={styles.positionLabel}>Move to position</Text>
										<Picker
											accessibilityHint="Select a position in the remaining route"
											accessibilityLabel={`Move ${stop.name} to position; currently position ${index + 1}`}
											selectedValue={index}
											onValueChange={nextIndex => moveStop(stop.id, nextIndex)}
											options={positionOptions}
											width={112}
										/>
									</View>
								</View>
							);
						})
					}
					{
						!orderedStopIds.length && (
							<Text style={styles.subtleText}>No stops are available to reorder.</Text>
						)
					}
				</ScrollView>
				<Text style={styles.reasonLabel}>Reason</Text>
				<TextInput
					value={reason}
					onChange={setReason}
					placeholder="e.g. opening hours, fresh food needs earlier delivery"
					multiline={true}
					maxLength={300}
					style={styles.reasonInput}
				/>
				{/* `secondary` is the solid Button style; the submit must outweigh Cancel. */}
				<Button
					label="Save stop order"
					onPress={submit}
					loading={loading}
					disabled={loading || !hasOrderChanged || !reason.trim()}
					secondary={true}
					style={styles.submitButton}
				/>
				<Button
					label="Cancel"
					onPress={close}
					style={styles.cancelButton}
				/>
			</View>
		</Dialog>
	);
};

const createStyles = (theme) => ({
	subtleText: {
		color: theme.text2,
		fontSize: 13,
		marginBottom: 8,
	},
	stopList: {
		maxHeight: 240,
		marginBottom: 8,
	},
	stopRow: {
		flexDirection: 'row',
		alignItems: 'center',
		backgroundColor: theme.mainBg3,
		borderRadius: 8,
		paddingVertical: 8,
		paddingHorizontal: 8,
		marginBottom: 6,
		borderWidth: 1,
		borderColor: theme.border3,
	},
	stopRowText: {
		flex: 1,
		flexShrink: 1,
		fontSize: 14,
		lineHeight: 19,
		color: theme.text1,
		paddingRight: 8,
	},
	stopOrder: {
		width: 26,
		height: 26,
		borderRadius: 13,
		alignItems: 'center',
		justifyContent: 'center',
		backgroundColor: theme.mainBg1,
		marginRight: 8,
	},
	stopOrderText: {
		fontSize: 12,
		fontWeight: '700',
		color: theme.text1,
	},
	positionControl: {
		width: 112,
		flexShrink: 0,
	},
	positionLabel: {
		color: theme.text2,
		fontSize: 11,
		marginBottom: 2,
	},
	reasonLabel: {
		fontSize: 14,
		fontWeight: '600',
		color: theme.text1,
		marginBottom: 4,
	},
	reasonInput: {
		minHeight: 64,
		textAlignVertical: 'top',
		marginBottom: 4,
	},
	submitButton: {
		marginTop: 8,
	},
	cancelButton: {
		marginTop: 6,
	},
});

export default NextStopReasonDialog;
